import { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useEntitlements } from "@/contexts/EntitlementsContext";
import {
  getNavRailLocked,
  readNavRailLockedSync,
  saveNavRailLocked,
} from "@/api/appShellPreferencesApi";
import { useAuthContext } from "@/contexts/AuthContext";
import { useAgentPresence } from "@/contexts/AgentPresenceContext";
import { useDashboardPageAccess } from "@/hooks/useDashboardPageAccess";
import { PATHNAME_TO_FEATURE_PAGE_KEY, PATHNAME_TO_PAGE_KEY } from "@/lib/dashboardPages";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import { isHumanAgentInboxLive } from "@/lib/inboxLiveConfig";

// The first six entries are the offerings named on sentrodesk.com/pricing, in
// the order the pricing page lists them, and labelled with the same words — a
// customer who buys "Sentiment Analysis" should find exactly that in the rail.
//
// `rbacPath` points at the page whose RBAC permission really governs the entry,
// for rows that render a plan gate and then forward to an existing page.
const NAV_ITEMS = [
  { path: "/", icon: "ri-home-5-line", activeIcon: "ri-home-5-fill", label: "Home", exact: true },

  { path: "/bot-chats", icon: "ri-robot-line", activeIcon: "ri-robot-fill", label: "AI Chatbot" },
  { path: "/chat", icon: "ri-chat-3-line", activeIcon: "ri-chat-3-fill", label: "Live Chat" },
  { path: "/tickets", icon: "ri-ticket-2-line", activeIcon: "ri-ticket-2-fill", label: "Tickets" },
  { path: "/knowledge-base", icon: "ri-book-open-line", activeIcon: "ri-book-open-fill", label: "Knowledge Base", rbacPath: "/settings" },
  { path: "/sentiment", icon: "ri-emotion-line", activeIcon: "ri-emotion-fill", label: "Sentiment Analysis", rbacPath: "/analytics" },
  { path: "/auto-qa", icon: "ri-shield-check-line", activeIcon: "ri-shield-check-fill", label: "AutoQA", rbacPath: "/analytics" },

  { path: "/search", icon: "ri-search-line", activeIcon: "ri-search-fill", label: "Search" },
  { path: "/users", icon: "ri-group-line", activeIcon: "ri-group-fill", label: "Users" },
  { path: "/analytics", icon: "ri-bar-chart-2-line", activeIcon: "ri-bar-chart-2-fill", label: "Analytics" },
  { path: "/integrations", icon: "ri-plug-line", activeIcon: "ri-plug-fill", label: "Integrations" },
  { path: "/widget-generator", icon: "ri-code-s-slash-line", activeIcon: "ri-code-s-slash-fill", label: "Widget & SDK" },
  { path: "/email-service", icon: "ri-mail-settings-line", activeIcon: "ri-mail-settings-fill", label: "Email service" },
  { path: "/docs-site", icon: "ri-global-line", activeIcon: "ri-global-fill", label: "Docs site" },
  { path: "/personal-settings", icon: "ri-user-settings-line", activeIcon: "ri-user-settings-fill", label: "Personal settings" },
  { path: "/settings", icon: "ri-settings-3-line", activeIcon: "ri-settings-3-fill", label: "Settings" },
];

const RAIL_NARROW_PX = 72;
const RAIL_WIDE_PX = 232;

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const { signOut } = useAuthContext();
  const { status: presenceStatus, setStatus: setPresenceStatus, busy: presenceBusy } = useAgentPresence();
  const { canAccessPage, loading: pageAccessLoading } = useDashboardPageAccess();
  const { isPageLocked } = useEntitlements();
  const [railHoverOpen, setRailHoverOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [locked, setLocked] = useState(() => readNavRailLockedSync());
  const wide = locked || railHoverOpen || mobileNavOpen;
  const showPresenceToggle = isHumanAgentInboxLive();

  // Filter nav by dashboard RBAC matrix (`pageKey`). Never show full rail while access is loading.
  const visibleNavItems = useMemo(() => {
    if (pageAccessLoading) {
      return NAV_ITEMS.filter(
        (item) => !PATHNAME_TO_PAGE_KEY[item.rbacPath || item.path]
      );
    }
    return NAV_ITEMS.filter((item) => {
      // Plan-gated rows carry no RBAC key of their own, so they inherit the
      // permission of the page they forward to.
      const pk = PATHNAME_TO_PAGE_KEY[item.rbacPath || item.path];
      if (!pk) return true;
      return canAccessPage(pk);
    });
  }, [canAccessPage, pageAccessLoading]);

  const handleLogout = useCallback(() => {
    signOut();
    navigate("/login", { replace: true });
  }, [navigate, signOut]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await getNavRailLocked();
      if (!cancelled) setLocked(Boolean(data?.locked));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleLocked = useCallback(async () => {
    const next = !locked;
    setLocked(next);
    if (next) setRailHoverOpen(true);
    await saveNavRailLocked({ locked: next });
  }, [locked]);

  useEffect(() => {
    if (locked || !railHoverOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setRailHoverOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [locked, railHoverOpen]);

  return (
    <div className="flex h-screen min-w-0 max-w-full flex-col overflow-hidden bg-app-canvas md:flex-row">
      <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-2 md:hidden dark:border-slate-800 dark:bg-slate-900">
        <button
          type="button"
          className="ui-btn-icon !h-9 !w-9"
          aria-label="Open navigation"
          onClick={() => setMobileNavOpen(true)}
        >
          <i className="ri-menu-line text-lg" aria-hidden />
        </button>
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">SentroDesk</span>
      </header>

      {mobileNavOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-[70] bg-slate-900/40 md:hidden"
          aria-label="Close navigation"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      {/* Widen hover target from the main canvas — slide pointer to the left edge to peek the rail */}
      {!wide && (
        <div
          className="pointer-events-auto fixed inset-y-0 left-0 z-[60] hidden w-3 bg-transparent md:block md:w-4"
          aria-hidden
          onMouseEnter={() => setRailHoverOpen(true)}
        />
      )}

      <div
        className={`min-w-0 shrink-0 flex-col overflow-x-clip transition-[width] duration-200 ease-out ${
          mobileNavOpen ? "fixed inset-y-0 left-0 z-[80] flex w-[232px] md:static md:z-50" : "hidden md:flex"
        }`}
        style={{ width: mobileNavOpen ? RAIL_WIDE_PX : wide ? RAIL_WIDE_PX : RAIL_NARROW_PX }}
        onMouseEnter={() => setRailHoverOpen(true)}
        onMouseLeave={() => {
          if (!locked) setRailHoverOpen(false);
        }}
      >
        <aside
          className="flex h-full min-h-0 w-full flex-col overflow-x-clip border-r border-slate-200/90 bg-white py-5 shadow-[4px_0_24px_-8px_rgba(15,23,42,0.06)] dark:border-slate-800/90 dark:bg-slate-900 dark:shadow-[4px_0_24px_-8px_rgba(0,0,0,0.35)]"
          aria-label="Primary navigation"
        >
          <div
            className={`relative mb-6 flex w-full min-w-0 items-center px-3 ${wide ? "" : "justify-center"}`}
          >
            <WorkspaceSwitcher wide={wide} />
          </div>

          <nav className="relative flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2" role="navigation">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                aria-label={item.label}
                title={item.label}
                onClick={() => setMobileNavOpen(false)}
                className={({ isActive }) =>
                  `group relative flex items-center overflow-x-clip rounded-2xl py-2.5 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 ${
                    wide ? "gap-3 px-2.5" : "h-11 w-11 justify-center px-0"
                  } ${
                    isActive
                      ? "bg-teal-600 text-white shadow-sm shadow-teal-900/20 ring-1 ring-white/10"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                  }`
                }
              >
                {({ isActive }) => {
                  const planLocked = isPageLocked(
                    PATHNAME_TO_FEATURE_PAGE_KEY[item.path] ||
                      PATHNAME_TO_PAGE_KEY[item.path]
                  );
                  return (
                    <>
                      <i className={`${isActive ? item.activeIcon : item.icon} relative z-10 shrink-0 text-xl ${planLocked ? "opacity-60" : ""}`} aria-hidden />
                      {wide && (
                        <span className={`relative z-10 min-w-0 flex-1 truncate text-sm font-medium ${planLocked ? "opacity-70" : ""}`}>
                          {item.label}
                        </span>
                      )}
                      {wide && planLocked && (
                        <i className="ri-lock-2-line relative z-10 shrink-0 text-sm opacity-70" aria-label="Not included in your plan" />
                      )}
                      {!wide && planLocked && (
                        // The collapsed rail has no room for a label, so the
                        // lock rides the icon instead of disappearing.
                        <i
                          className="ri-lock-2-line pointer-events-none absolute -right-0.5 -top-0.5 z-20 rounded-full bg-white text-[10px] leading-none text-slate-500 ring-2 ring-white dark:bg-slate-900 dark:text-slate-400 dark:ring-slate-900"
                          aria-label="Not included in your plan"
                        />
                      )}
                    </>
                  );
                }}
              </NavLink>
            ))}
          </nav>

          <div className="relative mt-auto flex flex-col gap-2 border-t border-slate-200 px-2 pt-4 dark:border-slate-800">
            {showPresenceToggle && wide && (
              <button
                type="button"
                disabled={presenceBusy}
                onClick={() =>
                  void setPresenceStatus(presenceStatus === "available" ? "away" : "available")
                }
                className={`flex w-full items-center gap-3 rounded-2xl px-2.5 py-2.5 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:opacity-50 dark:focus-visible:ring-offset-slate-900 ${
                  presenceStatus === "available"
                    ? "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-100 dark:ring-emerald-800/60"
                    : "bg-slate-50 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
                }`}
                aria-pressed={presenceStatus === "available"}
                title={
                  presenceStatus === "available"
                    ? "You are Online — accepting new chats. Click to go Away."
                    : "You are Away — not accepting new chats. Click to go Online."
                }
              >
                <span
                  className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                    presenceStatus === "available" ? "bg-emerald-500" : "bg-slate-400"
                  }`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  {presenceStatus === "available" ? "Online" : "Away"}
                </span>
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  {presenceStatus === "available" ? "Active" : "Inactive"}
                </span>
              </button>
            )}
            {showPresenceToggle && !wide && (
              <button
                type="button"
                disabled={presenceBusy}
                onClick={() =>
                  void setPresenceStatus(presenceStatus === "available" ? "away" : "available")
                }
                className="mx-auto flex h-10 w-10 items-center justify-center rounded-2xl text-slate-500 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:opacity-50 dark:hover:bg-slate-800"
                aria-pressed={presenceStatus === "available"}
                title={
                  presenceStatus === "available"
                    ? "Online (accepting chats) — click for Away"
                    : "Away (not accepting) — click for Online"
                }
              >
                <span
                  className={`h-2.5 w-2.5 rounded-full ${
                    presenceStatus === "available" ? "bg-emerald-500" : "bg-slate-400"
                  }`}
                  aria-hidden
                />
              </button>
            )}
            {wide && (
              <button
                type="button"
                onClick={() => void toggleLocked()}
                className={`flex w-full items-center gap-3 rounded-2xl px-2.5 py-2.5 text-left text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900 ${
                  locked
                    ? "bg-teal-50 text-teal-900 ring-1 ring-teal-200 dark:bg-slate-800 dark:text-teal-100 dark:ring-slate-600"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100"
                }`}
                aria-pressed={locked}
                title={locked ? "Nav rail stays open (locked)" : "Keep nav rail open"}
              >
                <i
                  className={`text-lg ${locked ? "ri-pushpin-fill text-teal-600 dark:text-teal-400" : "ri-pushpin-line text-slate-500 dark:text-slate-400"}`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">{locked ? "Rail locked open" : "Lock rail open"}</span>
              </button>
            )}            {!wide && (
              <button
                type="button"
                onClick={() => void toggleLocked()}
                className="mx-auto flex h-10 w-10 items-center justify-center rounded-2xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-teal-300"
                aria-pressed={locked}
                title={locked ? "Unlock nav rail" : "Lock nav rail open"}
              >
                <i
                  className={`${locked ? "ri-pushpin-fill text-teal-600 dark:text-teal-400" : "ri-pushpin-line"} text-lg`}
                  aria-hidden
                />
              </button>
            )}
            <div className="h-px w-full bg-slate-200 dark:bg-slate-800" />
            {wide && (
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-2xl px-2.5 py-2.5 text-left text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100 dark:focus-visible:ring-offset-slate-900"
              >
                <i className="ri-logout-box-r-line text-lg text-slate-500 dark:text-slate-400" aria-hidden />
                <span className="min-w-0 flex-1">Log out</span>
              </button>
            )}
            {!wide && (
              <button
                type="button"
                onClick={handleLogout}
                title="Log out"
                className="mx-auto flex h-10 w-10 items-center justify-center rounded-2xl text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              >
                <i className="ri-logout-box-r-line text-lg" aria-hidden />
              </button>
            )}
          </div>
        </aside>
      </div>

      <main className="app-content-surface relative min-h-0 min-w-0 flex-1 overflow-hidden bg-app-canvas">
        {children}
      </main>
    </div>
  );
}
